import React, { useState } from "react";
import { personalInfo } from "../data/mock";
import { Mail, Linkedin, MapPin, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API_BASE_URL } from "../config/api";
import { Textarea } from "./ui/textarea";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [submitStatus, setSubmitStatus] = useState({ message: "", isError: false, isSubmitting: false });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ message: "", isError: false, isSubmitting: true });
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/contact/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus({ 
          message: data.message || "Thank you for your message! I'll get back to you soon. 🔥", 
          isError: false,
          isSubmitting: false
        });
        // Reset form
        setFormData({ name: "", email: "", subject: "", message: "" });
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSubmitStatus({ message: "", isError: false, isSubmitting: false });
        }, 5000);
      } else {
        setSubmitStatus({ 
          message: data.message || "Failed to send message. Please try again.", 
          isError: true,
          isSubmitting: false
        });
      }
    } catch (error) {
      setSubmitStatus({ 
        message: "Connection error. Please try again later.", 
        isError: true,
        isSubmitting: false
      });
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="text-center mb-16">
        <h2 className="text-5xl lg:text-6xl font-light tracking-tight text-black mb-6">
          Let's Connect
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto font-light leading-relaxed">
          If you'd like to connect about future opportunities, collaborations, or innovative projects, please send me an email.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-16">
        {/* Contact Information */}
        <div className="space-y-12">
          <div>
            <h3 className="text-2xl font-light text-black mb-8 tracking-wide">
              Get in Touch
            </h3>
            
            <div className="space-y-6">
              <div className="flex items-center space-x-4 group hover:translate-x-2 transition-transform duration-300">
                <div className="p-3 bg-gray-100 rounded-lg group-hover:bg-black group-hover:text-white transition-colors duration-300">
                  <Mail size={20} />
                </div>
                <div>
                  <div className="text-sm text-gray-500 font-light uppercase tracking-wider">Email</div>
                  <a 
                    href={`mailto:${personalInfo.email}`}
                    className="text-black font-light hover:text-gray-600 transition-colors"
                  >
                    {personalInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-center space-x-4 group hover:translate-x-2 transition-transform duration-300">
                <div className="p-3 bg-gray-100 rounded-lg group-hover:bg-black group-hover:text-white transition-colors duration-300">
                  <Linkedin size={20} />
                </div>
                <div>
                  <div className="text-sm text-gray-500 font-light uppercase tracking-wider">LinkedIn</div>
                  <a 
                    href={personalInfo.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-black font-light hover:text-gray-600 transition-colors"
                  >
                    linkedin.com/in/cherise-t-332aa31
                  </a>
                </div>
              </div>

              <div className="flex items-center space-x-4 group hover:translate-x-2 transition-transform duration-300">
                <div className="p-3 bg-gray-100 rounded-lg group-hover:bg-black group-hover:text-white transition-colors duration-300">
                  <MapPin size={20} />
                </div>
                <div>
                  <div className="text-sm text-gray-500 font-light uppercase tracking-wider">Location</div>
                  <span className="text-black font-light">{personalInfo.location}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Philosophy Reminder */}
          <div className="bg-gray-50 p-8 rounded-lg">
            <h4 className="text-lg font-light text-black mb-4 tracking-wide">
              The Philosophy of Alchemizing & Creating
            </h4>
            <p className="text-gray-600 font-light leading-relaxed italic mb-4">
              "{personalInfo.philosophy}"
            </p>
            <div className="text-sm font-light text-black tracking-wide">
              {personalInfo.motto}
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white border border-gray-200 rounded-lg p-8">
          <h3 className="text-2xl font-light text-black mb-8 tracking-wide">
            Send a Message
          </h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-light text-gray-700 mb-2 tracking-wide">
                  Name *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full border-gray-200 focus:border-black transition-colors"
                  placeholder="Your full name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-light text-gray-700 mb-2 tracking-wide">
                  Email *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full border-gray-200 focus:border-black transition-colors"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-light text-gray-700 mb-2 tracking-wide">
                Subject *
              </label>
              <Input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className="w-full border-gray-200 focus:border-black transition-colors"
                placeholder="Project discussion, collaboration opportunity, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-light text-gray-700 mb-2 tracking-wide">
                Message *
              </label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={6}
                className="w-full border-gray-200 focus:border-black transition-colors resize-none"
                placeholder="Tell me about your project, goals, or how we might work together..."
              />
            </div>

            <Button 
              type="submit"
              disabled={submitStatus.isSubmitting}
              className="w-full bg-black text-white hover:bg-gray-800 transition-colors duration-300 py-6 text-base font-light tracking-wide group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>{submitStatus.isSubmitting ? 'Sending...' : 'Send Message'}</span>
              <Send size={18} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
          </form>

          {/* Status Message */}
          {submitStatus.message && (
            <div className={`mt-6 p-4 rounded-lg text-sm ${
              submitStatus.isError 
                ? 'bg-red-100 text-red-800 border border-red-200' 
                : 'bg-green-100 text-green-800 border border-green-200'
            }`}>
              {submitStatus.message}
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 font-light">
              Typically responds within 24 hours
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;